import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PlayCircle, FileText } from "lucide-react";
import {
    CHANGE_ID,
    PACKAGE_V2_REVISIONS,
    PARTICIPANTS,
    READINESS_STATES,
    IMPACT_SUMMARY,
    RECEIPTS,
} from "./data";
import { StatusChip, Kicker, DownloadReceiptBtn, ChainRow } from "./ui";

const STEWARD_CHAIN = ["IMPACT REVIEWED", "CHANGE CLARIFIED", "FINAL PACKAGE APPROVED"];
const NODE_DELAYS = { orig: 1, inter: 0, recv: 2, settle: 3 };

export const ActActivation = ({ scenario }) => {
    const [stewardStep, setStewardStep] = useState(-1);
    const [simTick, setSimTick] = useState(-1);
    const approved = stewardStep >= STEWARD_CHAIN.length - 1;
    const allScheduled = simTick >= READINESS_STATES.length + 3;

    useEffect(() => {
        if (stewardStep < 0 || stewardStep >= STEWARD_CHAIN.length - 1) return;
        const t = setTimeout(() => setStewardStep((s) => s + 1), 800);
        return () => clearTimeout(t);
    }, [stewardStep]);

    useEffect(() => {
        if (simTick < 0 || simTick >= READINESS_STATES.length + 3) return;
        const t = setTimeout(() => setSimTick((s) => s + 1), 650);
        return () => clearTimeout(t);
    }, [simTick]);

    return (
        <section data-testid="act-activation">
            <Kicker>Act 3 — Two-way clarification and activation</Kicker>
            <h2 className="mt-2 text-2xl font-medium tracking-tight text-slate-900">
                Impact evidence returns; the steward clarifies; activation is coordinated
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                The Standards Steward receives the four bounded impact receipts. This is
                not a vote — the steward remains authoritative for the standard. The
                feedback informs implementation design and timing.
            </p>

            {scenario === "legacy" && (
                <div
                    data-testid="activation-legacy-note"
                    className="mt-4 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm leading-relaxed text-red-900"
                >
                    The RED result from the Settlement / Reporting Role directly changed
                    this plan: reporting activation was separated from message
                    activation, the reporting-role transition period was extended, and
                    treatment of outstanding records was defined.
                </div>
            )}

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
                {/* Steward response */}
                <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="steward-response-card">
                    <div className="flex flex-wrap items-center gap-2">
                        {IMPACT_SUMMARY.slice(0, 3).map((s) => (
                            <StatusChip key={s.label} tone={s.tone}>
                                {s.count} {s.label}
                            </StatusChip>
                        ))}
                        <span className="font-mono text-[11px] uppercase tracking-wide text-slate-400">
                            received by steward
                        </span>
                    </div>

                    <p className="mt-5 font-mono text-sm font-medium tracking-tight text-slate-900">
                        CHANGE PACKAGE {CHANGE_ID} — VERSION 2
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                        Revised in response to impact, clarification and dependency
                        evidence:
                    </p>
                    <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
                        {PACKAGE_V2_REVISIONS.map((r) => (
                            <li key={r} className="flex gap-2">
                                <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-950" strokeWidth={1.5} />
                                {r}
                            </li>
                        ))}
                    </ul>

                    <div className="mt-6">
                        <ChainRow steps={STEWARD_CHAIN} activeIndex={stewardStep} testIdPrefix="steward-chain" />
                    </div>

                    <button
                        onClick={() => setStewardStep(0)}
                        disabled={stewardStep >= 0}
                        data-testid="issue-v2-btn"
                        className="mt-5 inline-flex items-center gap-2 rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900 disabled:cursor-default disabled:opacity-50"
                    >
                        Issue revised package (version 2)
                    </button>
                </div>

                {/* Local simulation and readiness */}
                <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="readiness-card">
                    <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                        Local simulation testing — per participant
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">
                        Nodes do not need identical internal software. Each node
                        demonstrates compatibility with the final published package.
                    </p>

                    <div className="mt-4 space-y-2.5">
                        {PARTICIPANTS.map((p) => {
                            const local = Math.min(
                                Math.max(simTick - NODE_DELAYS[p.id], -1),
                                READINESS_STATES.length - 1
                            );
                            const state = local >= 0 ? READINESS_STATES[local] : "AWAITING FINAL PACKAGE";
                            const done = local === READINESS_STATES.length - 1;
                            return (
                                <div key={p.id} className="flex items-center justify-between gap-2">
                                    <span className="text-sm text-slate-700">{p.name}</span>
                                    <StatusChip
                                        tone={done ? "green" : local >= 0 ? "blue" : "slate"}
                                        testId={`readiness-${p.id}`}
                                    >
                                        {state}
                                    </StatusChip>
                                </div>
                            );
                        })}
                    </div>

                    <button
                        onClick={() => setSimTick(0)}
                        disabled={!approved || simTick >= 0}
                        data-testid="run-simulation-btn"
                        className="mt-5 inline-flex items-center gap-2 rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900 disabled:cursor-default disabled:opacity-50"
                    >
                        <PlayCircle className="h-4 w-4" strokeWidth={1.5} />
                        Run local simulation tests
                    </button>
                    {!approved && (
                        <p className="mt-2 text-xs text-slate-400">
                            Available once the final package is approved.
                        </p>
                    )}

                    {allScheduled && (
                        <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-5 rounded-sm border border-green-200 bg-green-50 px-4 py-3"
                            data-testid="network-ready-banner"
                        >
                            <p className="font-mono text-xs font-medium uppercase tracking-wide text-green-800">
                                NETWORK READY FOR CONTROLLED ACTIVATION
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-green-900">
                                The necessary role conditions are satisfied. Message
                                activation proceeds; reporting activation follows its
                                extended transition period.
                            </p>
                        </motion.div>
                    )}

                    <div className="mt-5 flex items-center justify-between gap-2 border-t border-slate-100 pt-4">
                        <span className="font-mono text-[10px] uppercase tracking-wide text-slate-400">
                            Signed readiness receipt
                        </span>
                        <DownloadReceiptBtn
                            json={RECEIPTS[2].json}
                            filename="readiness-receipt-orig.json"
                            testId="download-readiness-receipt"
                            label="Readiness receipt"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};
